import { Box, Divider, Stack, Typography } from "@mui/material";
import InternAppReviewCard from "./InternAppReviewCard";
import InternScheduledBuildCard from "./InternScheduledBuildCard";

interface PendingChange {
  field: string;
  new_value?: string;
  changed_at: string;
}

interface ScheduledBuildData {
  id: string;
  platform: string;
  changesCount: number;
  scheduledFor: string;
  lastChangeAt: string;
  status: string;
  pendingChanges?: PendingChange[];
}

interface ReviewData {
  id: string;
  platform: string;
  appVersion: string;
  buildNumber: string;
  status: string;
  statusDisplay: string;
  submittedAt: string | null;
  reviewedAt: string | null;
}

interface Props {
  brandId: string;
  scheduledBuilds: ScheduledBuildData[];
  iosReview: ReviewData | null;
  androidReview: ReviewData | null;
  onRefresh?: () => void;
}

/**
 * Single surface for schedule + review so side-by-side content
 * never leaves uneven card gaps.
 */
const InternBuildStatusSection = ({
  brandId,
  scheduledBuilds,
  iosReview,
  androidReview,
  onRefresh,
}: Props) => {
  const primarySchedule = scheduledBuilds[0] ?? null;
  const secondarySchedules = scheduledBuilds.slice(1);

  return (
    <Box
      sx={{
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid var(--intern-border-default)",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ color: "var(--intern-text-primary)", fontSize: "1rem" }}
        >
          Status
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "var(--intern-text-tertiary)",
            fontSize: "0.75rem",
            mt: 0.25,
          }}
        >
          Upcoming auto-builds and latest store review
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: "var(--intern-border-default)",
              display: { xs: "none", md: "block" },
            }}
          />
        }
        sx={{ alignItems: "stretch" }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Scheduled
          </Typography>
          <Box sx={{ flex: 1 }}>
            <InternScheduledBuildCard
              brandId={brandId}
              scheduledBuild={primarySchedule}
              onRefresh={onRefresh}
              embedded
            />
          </Box>
          {secondarySchedules.map((schedule) => (
            <Box
              key={schedule.id}
              sx={{
                pt: 1.5,
                borderTop: "1px solid var(--intern-border-default)",
              }}
            >
              <InternScheduledBuildCard
                brandId={brandId}
                scheduledBuild={schedule}
                onRefresh={onRefresh}
                embedded
              />
            </Box>
          ))}
        </Box>

        <Divider
          sx={{
            borderColor: "var(--intern-border-default)",
            display: { xs: "block", md: "none" },
          }}
        />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              mb: 1.25,
            }}
          >
            App Review
          </Typography>
          <Box sx={{ flex: 1 }}>
            <InternAppReviewCard
              iosReview={iosReview}
              androidReview={androidReview}
              embedded
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default InternBuildStatusSection;
